/**
 * Research Mission Panel
 *
 * Collapsible panel that displays the research question, plan, and progress.
 * Pinned to the top of the chat area to keep context visible.
 */

import React, { useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Circle,
  CircleDot,
  Target,
  type LucideIcon,
} from 'lucide-react';
import { ResearchMission } from '../types';
import { formatCount } from '../utils/plural';

interface ResearchMissionPanelProps {
  mission: ResearchMission | null;
}

const ResearchMissionPanel: React.FC<ResearchMissionPanelProps> = ({ mission }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!mission) {
    return null;
  }

  const { query, hypothesis, context, plan, plan_progress, target_output } = mission;

  // Calculate plan progress percentage
  const planProgressPercent = plan_progress.total > 0
    ? Math.round((plan_progress.done / plan_progress.total) * 100)
    : 0;

  // Calculate hypothesis progress percentage
  const hypothesisProgressPercent = hypothesis?.progress?.total
    ? Math.round((hypothesis.progress.complete / hypothesis.progress.total) * 100)
    : 0;

  // Status icons for plan steps
  const planStatusIcons: Record<string, LucideIcon> = {
    done: Check,
    in_progress: CircleDot,
    pending: Circle,
  };

  // Status colors for plan steps
  const planStatusColors: Record<string, string> = {
    done: 'text-success',
    in_progress: 'text-accent',
    pending: 'text-gray-400'
  };

  // Status icons for hypothesis components
  const componentStatusIcons: Record<string, LucideIcon> = {
    complete: Check,
    researching: CircleDot,
    pending: Circle,
  };

  // Status colors for hypothesis components
  const componentStatusColors: Record<string, string> = {
    complete: 'text-success',
    researching: 'text-node-definition',
    pending: 'text-gray-400'
  };

  // Necessity badge colors
  const necessityColors: Record<string, string> = {
    required: 'bg-danger-bg text-danger',
    recommended: 'bg-warning-bg text-warning',
    optional: 'bg-gray-100 text-gray-600'
  };

  return (
    <div className="border-b border-gray-200 bg-gray-50/50 mb-4">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100/50 transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Target size={20} strokeWidth={1.5} className="flex-shrink-0 text-fg-secondary" aria-hidden="true" />
          <span className="font-medium text-gray-900 truncate">
            {query}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {plan.length > 0 && (
            <span className="text-sm text-gray-600">
              {plan_progress.done}/{plan_progress.total}
            </span>
          )}
          <span className="text-gray-400">
            {isExpanded
              ? <ChevronUp size={16} strokeWidth={1.5} aria-hidden="true" />
              : <ChevronDown size={16} strokeWidth={1.5} aria-hidden="true" />}
          </span>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Hypothesis - Show first if exists */}
          {hypothesis && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-gray-500 uppercase">
                  Research framework
                </div>
                {hypothesis.progress && (
                  <div className="text-xs text-gray-600">
                    {hypothesisProgressPercent}% complete
                  </div>
                )}
              </div>

              {/* Core Framework */}
              <div className="text-sm text-gray-700 mb-3 bg-accent-tint p-2 rounded border-l-2 border-accent-border">
                {hypothesis.core_framework}
              </div>

              {/* Hypothesis Progress Bar */}
              {hypothesis.progress && hypothesis.progress.total > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-1.5 mb-3">
                  <div
                    className="bg-node-definition h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${hypothesisProgressPercent}%` }}
                  />
                </div>
              )}

              {/* Logical Components */}
              {hypothesis.logical_components.length > 0 && (
                <div className="space-y-1.5">
                  {hypothesis.logical_components.map((comp) => {
                    const StatusIcon = componentStatusIcons[comp.status] ?? Circle;
                    return (
                      <div
                        key={comp.component_id}
                        className="flex items-start gap-2 text-sm"
                      >
                        <StatusIcon
                          size={16}
                          strokeWidth={1.5}
                          className={`flex-shrink-0 ${componentStatusColors[comp.status]}`}
                          aria-hidden="true"
                        />
                        <span className="sr-only">Status: {comp.status.replace(/_/g, ' ')}</span>
                        <span className={`flex-1 ${comp.status === 'complete' ? 'text-gray-500' : 'text-gray-800'}`}>
                          {comp.purpose}
                        </span>
                        <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded ${necessityColors[comp.necessity]}`}>
                          {comp.necessity}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Context */}
          {Object.keys(context).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Context
              </div>
              <div className="text-sm text-gray-700">
                {Object.entries(context).map(([key, value]) => (
                  <span key={key} className="mr-3">
                    <span className="font-medium">{key}:</span> {value}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Plan */}
          {plan.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-gray-500 uppercase">
                  Research plan
                </div>
                <div className="text-xs text-gray-600">
                  {planProgressPercent}% complete
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-1.5 mb-3">
                <div
                  className="bg-accent h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${planProgressPercent}%` }}
                />
              </div>

              {/* Plan Steps */}
              <div className="space-y-1">
                {plan.map((step) => {
                  const StatusIcon = planStatusIcons[step.status] ?? Circle;
                  return (
                    <div
                      key={step.step_id}
                      className="flex items-start gap-2 text-sm"
                    >
                      <StatusIcon
                        size={16}
                        strokeWidth={1.5}
                        className={`flex-shrink-0 ${planStatusColors[step.status]}`}
                        aria-hidden="true"
                      />
                      <span className="sr-only">Status: {step.status.replace(/_/g, ' ')}</span>
                      <span className="flex-shrink-0 text-gray-500 font-mono text-xs">
                        {step.step_id}.
                      </span>
                      <span className={`flex-1 ${step.status === 'done' ? 'text-gray-600' : 'text-gray-900'}`}>
                        {step.description}
                      </span>
                      {step.findings_count > 0 && (
                        <span className="flex-shrink-0 text-xs text-gray-500">
                          [{formatCount(step.findings_count, { one: 'finding', other: 'findings' })}]
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Target Output */}
          {target_output && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Target
              </div>
              <div className="text-sm text-gray-700">
                {target_output.type.replace(/_/g, ' ')}
                {target_output.audience && ` for ${target_output.audience}`}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResearchMissionPanel;
